import { Seo } from "@/components/Seo";
import { Reveal, SectionHead, Marquee } from "@/components/System";
import { ASSETS } from "@/data/content";
import { motion } from "framer-motion";

const EVOLUTION = [
    { name: "AI", desc: "The first generation of AI could think." },
    { name: "AGENT", desc: "The next generation could act." },
    { name: "AUTONOMOUS AGENT", desc: "The next generation will operate autonomously." },
    { name: "PRIVATE AGENT", desc: "Autonomy over sensitive work requires privacy." },
    { name: "VERIFIABLE AGENT", desc: "Privacy without proof demands blind trust. Verification removes it." },
    { name: "ECONOMIC AGENT", desc: "A verified agent can be trusted with economic action." },
];

const Thesis = () => (
    <div>
        <Seo path="/thesis" />
        <section className="relative overflow-hidden px-4 pb-24 pt-36 sm:px-6">
            <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
            <div className="relative mx-auto max-w-5xl">
                <div className="vm-micro mb-6">AGENTVM://THESIS</div>
                <h1 className="text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
                    {["THE PROBLEM IS NOT", "INTELLIGENCE."].map((l, i) => (
                        <span key={l} className="block overflow-hidden pb-1">
                            <motion.span className="block text-[#F5F9FF]" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ delay: 0.15 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
                                {l}
                            </motion.span>
                        </span>
                    ))}
                    <span className="block overflow-hidden pb-1">
                        <motion.span className="block text-[#00D9FF] vm-glow" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
                            THE PROBLEM IS TRUST.
                        </motion.span>
                    </span>
                </h1>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="mt-8 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg"
                >
                    AI agents are becoming capable of autonomous action. They process private information, make
                    decisions, execute workflows, interact with financial systems, and communicate with other agents.
                    But autonomous action creates a fundamental problem: trust.
                </motion.p>
            </div>
        </section>

        <Marquee items={["WHO EXECUTED THE WORK?", "WHAT HAPPENED?", "WHERE DID IT HAPPEN?", "WHICH SOFTWARE EXECUTED?", "WHICH HARDWARE EXECUTED?", "CAN THE RESULT BE INDEPENDENTLY VERIFIED?"]} />

        <section className="mx-auto max-w-5xl px-4 py-24 sm:px-6">
            <SectionHead index="THS.01" title="THE EVOLUTION OF AGENCY." sub="Each step up this ladder increases capability — and increases the cost of getting trust wrong." />
            <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
                <div className="flex flex-col">
                    {EVOLUTION.map((e, i) => (
                        <Reveal key={e.name} delay={i * 0.06}>
                            <div className="border border-[rgba(0,217,255,0.12)] bg-[#080F18]/50 px-5 py-4">
                                <div className="flex items-baseline justify-between gap-4">
                                    <span className="font-mono text-sm tracking-[0.18em] text-[#F5F9FF]">{e.name}</span>
                                    <span className="font-mono text-[9px] tracking-[0.2em] text-[#00D9FF]/60">GEN {String(i + 1).padStart(2, "0")}</span>
                                </div>
                                <p className="mt-2 text-sm text-[#B8C7D9]/80">{e.desc}</p>
                            </div>
                            {i < EVOLUTION.length - 1 && (
                                <div className="pl-6" aria-hidden="true">
                                    <svg width="2" height="18" className="overflow-visible">
                                        <line x1="1" y1="0" x2="1" y2="18" stroke="rgba(0,217,255,0.35)" strokeWidth="1" className="vm-dash" />
                                    </svg>
                                </div>
                            )}
                        </Reveal>
                    ))}
                </div>
                <div className="flex flex-col gap-6">
                    <Reveal delay={0.1}>
                        <div className="vm-panel scanlines relative overflow-hidden">
                            <img src={ASSETS.art} alt="AgentVM runtime terminal visualization" className="h-72 w-full object-cover object-center opacity-90" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="vm-panel-soft p-6">
                            <div className="vm-micro mb-3">THE CORE QUESTION</div>
                            <p className="text-2xl font-semibold leading-snug text-[#F5F9FF] sm:text-3xl">
                                HOW DO YOU TRUST AN AGENT <span className="text-[#00D9FF]">WITHOUT EXPOSING EVERYTHING IT SAW?</span>
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        <section className="border-t border-[rgba(0,217,255,0.1)] bg-[#080F18]/40 py-24">
            <div className="mx-auto max-w-4xl px-4 sm:px-6">
                <SectionHead index="THS.02" title="TRUST IS AN ENGINEERING PROBLEM." />
                <div className="space-y-8">
                    {[
                        ["Trust cannot be a promise.", "A provider saying \"we ran your agent correctly\" is a claim, not evidence. Institutions cannot build on claims."],
                        ["Trust cannot require exposure.", "If verification demands seeing the private data, verification defeats the purpose of privacy. The two must be decoupled."],
                        ["Trust must be composable.", "When agents interact with other agents, each needs to verify the other. Verification must work machine-to-machine, at machine speed."],
                        ["Trust must survive autonomy.", "The more independent agents become, the more their actions must carry their own proof."],
                    ].map(([head, body], i) => (
                        <Reveal key={head} delay={i * 0.08}>
                            <div className="flex gap-5 border-l border-[#00D9FF]/30 pl-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-[#F5F9FF]">{head}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#B8C7D9]">{body}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-16 text-xl font-semibold leading-relaxed text-[#F5F9FF] sm:text-2xl">
                        AgentVM represents the trusted execution layer for this problem:{" "}
                        <span className="text-[#00D9FF]">private data in, verifiable action out.</span>
                    </p>
                </Reveal>
            </div>
        </section>
    </div>
);

export default Thesis;
